import { inject, Injectable } from '@angular/core';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { catchError, filter, map, mergeMap, switchMap } from 'rxjs/operators';
import { ENDPOINTS } from '../../../enums/endpoints.enum';
import { OnlineOnlyService } from '../../../services/online-only/online-only.service';
import { ODataSoftwareLicenceSet } from '../../../services/software-licences/software-licence.model';
import { UriUtilitiesService } from '../../../services/uri-utilities/uri-utilities.service';
import {
    deleteSoftwareLicences,
    hydrateSoftwareLicences,
    hydrateSoftwareLicencesSuccess,
    loadSoftwareLicences,
    loadSoftwareLicencesError,
    loadSoftwareLicencesSuccess,
} from '../actions/software-licences.actions';
import { selectSoftwareLicencesKeys, selectSoftwareLicencesLoaded } from '../selectors/software-licences.selector';
import { oDataToPlain } from '../odata-to-plain-coverter';
import { hydrateSlice, persistSlice, sliceHydrated } from '../../persistence';
import { SOFTWARE_LICENCES_FEATURE_KEY, SoftwareLicenceState } from '../software-licences-collection.state';
import { concatLatestFrom } from '@ngrx/operators';
import { selectEquipmentStates } from '../../notification/selectors/notification.selector';
import { notificationsUpdated$ } from '../../notification/notification-updates';
import { softwareLicenceEquipmentKeyForState } from '../software-licences.utils';

@Injectable()
export class SoftwareLicencesEffects {
    private readonly actions$ = inject(Actions);
    private readonly store = inject(Store);
    private readonly log: LogInterface = inject(LogInstanceToken);
    private readonly onlineOnlyService = inject(OnlineOnlyService);
    private readonly uriUtilities = inject(UriUtilitiesService);
    /** Ticks whenever the notification list was reloaded — see `notification-updates.ts`. */
    private readonly notificationsUpdated$ = notificationsUpdated$();

    public loadSoftwareLicences$ = createEffect(() =>
        this.actions$.pipe(
            ofType(loadSoftwareLicences),
            mergeMap(({ equipmentKey, matnr, sernr }) => {
                let query = ENDPOINTS.SoftwareLicences.toString();
                const filterValue = `Material eq '${encodeURIComponent(matnr)}' and SerialNo eq '${encodeURIComponent(sernr)}'`;
                query = this.uriUtilities.appendFilterValue(query, filterValue);
                query = this.uriUtilities.appendParameter(query, '$format=json');
                return this.onlineOnlyService.get<ODataSoftwareLicenceSet>(query).pipe(
                    map((data: ODataSoftwareLicenceSet[]) =>
                        loadSoftwareLicencesSuccess({
                            equipmentKey,
                            data: oDataToPlain(data),
                        }),
                    ),
                    catchError((error) => {
                        this.log.debug(
                            `SoftwareLicencesEffects: could not fetch licences for '${equipmentKey}': ${JSON.stringify(error)}`,
                        );

                        return of(loadSoftwareLicencesError({ equipmentKey }));
                    }),
                );
            }),
        ),
    );

    /**
     * Drops the cached licences of equipment that is no longer assigned to any notification. Both
     * sides of the comparison are read off the store the moment the notification list changed: the
     * assigned equipment now lives in the notification slice, so no service stands between this
     * effect and it.
     */
    public equipmentDeleted$ = createEffect(
        () =>
            this.notificationsUpdated$.pipe(
                concatLatestFrom(() => [
                    this.store.select(selectSoftwareLicencesKeys),
                    this.store.select(selectEquipmentStates),
                ]),
                map(([, licencesKeys, equipments]) => {
                    const equipmentsKeys = equipments.map(softwareLicenceEquipmentKeyForState);
                    const toDeleteKeys = licencesKeys.filter((licenceKey) => !equipmentsKeys.includes(licenceKey));

                    return deleteSoftwareLicences({ equipmentKeys: toDeleteKeys });
                }),
            ),
        {
            dispatch: false,
        },
    );

    /** Cache the slice whenever the backend load populates it. */
    public deleteSoftwareLicences$ = createEffect(() =>
        this.actions$.pipe(
            ofType(deleteSoftwareLicences),
            map(() => persistSlice({ featureKey: SOFTWARE_LICENCES_FEATURE_KEY })),
        ),
    );

    /** Cache the slice whenever the backend load populates it. */
    public persistSoftwareLicences$ = createEffect(() =>
        this.actions$.pipe(
            ofType(loadSoftwareLicencesSuccess),
            map(() => persistSlice({ featureKey: SOFTWARE_LICENCES_FEATURE_KEY })),
        ),
    );

    /** Rehydrate from the cache on init, but only while the backend has not yet populated the slice. */
    public hydrateSoftwareLicences$ = createEffect(() =>
        this.actions$.pipe(
            ofType(hydrateSoftwareLicences),
            switchMap(() => this.store.select(selectSoftwareLicencesLoaded)),
            filter((loaded) => !loaded),
            map(() => hydrateSlice({ featureKey: SOFTWARE_LICENCES_FEATURE_KEY })),
        ),
    );

    /** Bridge the generic hydrate result into the value-help slice, unless the backend already won. */
    public applyHydratedSoftwareLicences$ = createEffect(() =>
        this.actions$.pipe(
            ofType(sliceHydrated),
            filter(({ featureKey }) => featureKey === SOFTWARE_LICENCES_FEATURE_KEY),
            concatLatestFrom(() => this.store.select(selectSoftwareLicencesLoaded)),
            filter(([, loaded]) => !loaded),
            map(([{ data }]) =>
                hydrateSoftwareLicencesSuccess({
                    data: data as Record<string, SoftwareLicenceState[]> | undefined,
                }),
            ),
        ),
    );
}
